/**
 * Workspace tab bar (WS-1) — the strip across the top of the main pane listing
 * the open editors (UI canvas · a Query · a JS Object · Stato). Clicking a tab
 * makes it the active editor; the ✕ closes it (the UI tab is never closable).
 * Dumb/presentational: the parent owns the tab list + active key and passes a
 * pre-resolved `views` array (label + icon kind).
 */
import {
  AlarmClock,
  AppWindow,
  BookOpen,
  BookText,
  Boxes,
  Braces,
  Database,
  FileBarChart2,
  FileText,
  FlaskConical,
  Globe,
  Layers,
  MessageSquare,
  Mic,
  Network,
  Package,
  Server,
  Settings,
  Table2,
  Users,
  Variable,
  Webhook,
  X,
  Zap,
} from "lucide-react";

export interface WorkspaceTabView {
  key: string;
  // WS-4b — `globalState`/`settings` are the app-global main-pane contexts.
  // ADR-0046 — `jsLibraries`/`customComponents` are the app-global libraries.
  // ADR-0047 — `appQueries` is the app-global server-Query manager.
  // ADR-0048 single-IDE — `appTables`/`appDatasets`/`appMembers`/`appInterfaceChat`/
  // `appInterfaceVoice`/`appScheduledRules`/`appAutomations` are app-detail panels
  // migrated into the editor (ADR-0049: Chat & Voce are separate flat interfaces).
  kind: "ui" | "query" | "js" | "state" | "globalState" | "settings" | "jsLibraries" | "customComponents" | "appQueries" | "appTables" | "appDatasets" | "appLinkedDatasets" | "appLinkedKbs" | "appLinkedReports" | "appForms" | "appMembers" | "appInterfaceChat" | "appInterfaceVoice" | "appRest" | "appRestDocs" | "appGraphql" | "appGraphqlDocs" | "appScheduledRules" | "appAutomations";
  label: string;
  closable: boolean;
}

const KIND_ICON: Record<WorkspaceTabView["kind"], typeof AppWindow> = {
  ui: AppWindow,
  query: Database,
  js: Braces,
  state: Variable,
  globalState: Globe,
  settings: Settings,
  jsLibraries: Package,
  customComponents: Boxes,
  appQueries: Server,
  appTables: Table2,
  appDatasets: Layers,
  appLinkedDatasets: Database,
  appLinkedKbs: BookOpen,
  appLinkedReports: FileBarChart2,
  appForms: FileText,
  appMembers: Users,
  appInterfaceChat: MessageSquare,
  appInterfaceVoice: Mic,
  appRest: Webhook,
  appRestDocs: BookText,
  appGraphql: Network,
  appGraphqlDocs: FlaskConical,
  appScheduledRules: AlarmClock,
  appAutomations: Zap,
};

export function WorkspaceTabs({
  views,
  activeKey,
  onActivate,
  onClose,
}: {
  views: WorkspaceTabView[];
  activeKey: string;
  onActivate: (key: string) => void;
  onClose: (key: string) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5 overflow-x-auto border-b border-border bg-surface/40 px-2 py-1">
      {views.map((v) => {
        const Icon = KIND_ICON[v.kind];
        const active = v.key === activeKey;
        return (
          <div
            key={v.key}
            className={`group flex shrink-0 items-center gap-1.5 rounded-md pl-2.5 pr-1.5 ${
              active
                ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <button
              type="button"
              onClick={() => onActivate(v.key)}
              aria-pressed={active}
              className="flex items-center gap-1.5 py-1 text-xs"
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
              {v.label}
            </button>
            {v.closable && (
              <button
                type="button"
                aria-label={`Chiudi ${v.label}`}
                onClick={() => onClose(v.key)}
                className="rounded p-0.5 text-muted-foreground/50 hover:bg-border/40 hover:text-foreground"
              >
                <X className="h-3 w-3" strokeWidth={2} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
